import { useCallback, useMemo } from "react";

import "./ResponsiveReportCardGrid.css";

import { Box, Card, CardContent, Skeleton, Typography } from "@mui/material";
import { Stack } from "@mui/system";

import { formatDate } from "../../../utility/formatters";
import PaginationControls from "./PaginationControls";

export default function ResponsiveReportCardGrid({
  columns,
  rows,
  loading,
  sortModel,
  onSortModelChange,
  paginationModel,
  onPaginationModelChange,
}) {
  const pageSizeOptions = [5, 10, 25, 50];

  // Skeleton card renderer
  const renderSkeletonCard = useCallback(
    (index = 0) => {
      if (!Array.isArray(columns)) return null;
      const isEven = index % 2 === 0;
      const backgroundColor = isEven ? "background.paper" : "grey.100";
      let borderRadius = "10px";

      return (
        <Card
          className="responsive-data-grid-card"
          elevation={0}
          sx={{
            backgroundColor,
            borderRadius,
            "&:hover": {
              backgroundColor: "grey.100",
              transition: "background-color 0.2s ease",
            },
          }}
          key={`skeleton-${index}`}
        >
          <CardContent className="responsive-data-grid-card-content">
            <Stack spacing={1}>
              {columns.map((col, colIndex) => {
                if (!col) return null;
                const fieldKey = col.field || `skeleton-${colIndex}`;
                const headerName = col.headerText || col.headerName || col.field || "Field";
                return (
                  <Stack key={fieldKey} direction="row" justifyContent="flex-start" alignItems="center">
                    <Typography variant="subtitle2" width={150} marginBottom={2}>
                      {headerName}
                    </Typography>
                    <Box component="span">
                      <Skeleton variant="text" width={80} height={20} />
                    </Box>
                  </Stack>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      );
    },
    [columns]
  );

  const handleSort = (field) => {
    if (!onSortModelChange) return;
    const current = sortModel?.[0];
    if (current?.field === field) {
      onSortModelChange([{ field, sort: current.sort === "asc" ? "desc" : "asc" }]);
    } else {
      onSortModelChange([{ field, sort: "asc" }]);
    }
  };

  const sortedRows = useMemo(() => {
    if (!Array.isArray(rows) || !sortModel?.[0]?.field) return rows;
    const { field, sort } = sortModel[0];
    return [...rows].sort((a, b) => {
      const aV = a?.[field] ?? "";
      const bV = b?.[field] ?? "";
      if (typeof aV === "number" && typeof bV === "number") {
        return sort === "asc" ? aV - bV : bV - aV;
      }
      const aStr = String(aV).toLowerCase();
      const bStr = String(bV).toLowerCase();
      if (aStr < bStr) return sort === "asc" ? -1 : 1;
      if (aStr > bStr) return sort === "asc" ? 1 : -1;
      return 0;
    });
  }, [rows, sortModel]);

  const pageSize = paginationModel?.pageSize || 10;
  const page = paginationModel?.page || 1;
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  const defaultRenderCard = (row, idx) => (
    <div className="rrcg-card" key={idx}>
      {columns
        .filter((col) => col.visible !== false)
        .map((col) => {
          let cellValue;
          if (col.type === "date" && col.format) {
            cellValue = formatDate(row[col.field]);
          } else if (col.valueFormatter) {
            cellValue = col.valueFormatter(row[col.field], row);
          } else {
            cellValue = row[col.field] ?? "-";
          }
          return (
            <Stack direction="row" justifyContent="flex-start" alignItems="center" className="rrcg-row" key={col.field}>
              <Typography
                variant="subtitle2"
                width={150}
                style={{ cursor: onSortModelChange ? "pointer" : "default" }}
                onClick={() => handleSort(col.field)}
                className="subtitle2-value"
              >
                {col.headerText || col.headerName || col.field}
                {sortModel?.[0]?.field === col.field && (
                  <span style={{ marginLeft: 4 }}>{sortModel[0].sort === "asc" ? "▲" : "▼"}</span>
                )}
              </Typography>
              <Typography variant="span" className="rrcg-value">
                {cellValue}
              </Typography>
            </Stack>
          );
        })}
    </div>
  );

  const totalItems = sortedRows.length;
  return (
    <Box className="responsive-data-grid" mt={2}>
      <Stack spacing={2}>
        {loading
          ? [0].map((_, i) => <Box key={i}>{renderSkeletonCard(i)}</Box>)
          : paginatedRows.map((row, i) => defaultRenderCard(row, i))}
      </Stack>

      {!loading && totalItems >= 5 && (
        <PaginationControls
          totalItems={totalItems}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          pageSizeOptions={pageSizeOptions}
          loading={loading}
        />
      )}
    </Box>
  );
}
